import {supabase} from './supabase';
import {getPrimaryMembership} from './agentordinaryWorkspace';

export type RecordedSchedule={
 id:string;agentId:string;title:string;detail:string;
 start:string|null;time:string|null;frequency:string;
 status:string;enabled:boolean;kind:'task'|'trigger';timezone:string;
};

/** Read-only, bounded scheduled work from the authenticated AgentOrdinary workspace.
 * Does not schedule, resume, or trigger tasks and never makes external provider calls. */
export async function listRecordedSchedules(workspaceId:string):Promise<RecordedSchedule[]>{
 const membership=await getPrimaryMembership();
 if(membership.workspace_id!==workspaceId)throw new Error('Schedule workspace mismatch.');
 const start=new Date(Date.now()-14*86400000).toISOString();
 const end=new Date(Date.now()+45*86400000).toISOString();
 const [triggers,tasks]=await Promise.all([
  supabase.from('agentopia_triggers')
   .select('id,agent_id,name,kind,config,enabled,next_run_at,retired_at')
   .eq('workspace_id',workspaceId).eq('kind','Schedule')
   .is('retired_at',null).limit(100),
  supabase.from('agentopia_tasks')
   .select('id,agent_id,title,objective,status,scheduled_at,created_at')
   .eq('workspace_id',workspaceId)
   .not('scheduled_at','is',null)
   .gte('scheduled_at',start).lte('scheduled_at',end)
   .order('scheduled_at',{ascending:true}).limit(450)
 ]);
 if(triggers.error)throw triggers.error;
 if(tasks.error)throw tasks.error;
 const records:RecordedSchedule[]=[];
 for(const x of triggers.data||[]){
  const c=x.config&&typeof x.config==='object'&&!Array.isArray(x.config)?x.config as Record<string,unknown>:{};
  const clock=typeof c.time==='string'&&/^\d{1,2}:\d{2}$/.test(c.time)?c.time:null;
  records.push({
   id:'trigger-'+x.id,agentId:String(x.agent_id||''),title:String(x.name||'Scheduled action'),
   detail:typeof c.purpose==='string'?c.purpose:'Configured recurring schedule',
   start:x.next_run_at?String(x.next_run_at):null,time:clock,
   frequency:typeof c.frequency==='string'?c.frequency:'',
   status:x.enabled?'Configured':'Disabled',enabled:x.enabled===true,kind:'trigger',
   timezone:typeof c.timezone==='string'?c.timezone:'America/New_York'
  });
 }
 for(const x of tasks.data||[]){
  records.push({
   id:'task-'+x.id,agentId:String(x.agent_id||''),title:String(x.title||'Scheduled task'),
   detail:String(x.objective||'Recorded workspace task'),
   start:x.scheduled_at?String(x.scheduled_at):null,time:null,frequency:'',
   status:String(x.status||'Scheduled'),enabled:true,kind:'task',
   timezone:'America/New_York'
  });
 }
 return records;
}

/** Owner-scoped, read-only summary of the independent 30-minute safety audit.
 * This is a system pg_cron job, not a schedule or activation of the paused employee. */
export type WatchdogAuditStatus={
 agentId:string|null;configured:boolean;active:boolean;cadence:string;
 lastStatus:string|null;lastFinishedAt:string|null;agentPaused:boolean;
};
export async function listWatchdogAuditStatus(workspaceId:string):Promise<WatchdogAuditStatus>{
 const membership=await getPrimaryMembership();
 if(membership.workspace_id!==workspaceId)throw new Error('Schedule workspace mismatch.');
 const {data,error}=await supabase.rpc('agentopia_owner_watchdog_schedule_status',{p_workspace_id:workspaceId});
 if(error)throw error;
 const d=data as Record<string,unknown>|null;
 return {
  agentId:typeof d?.agent_id==='string'?d.agent_id:null,
  configured:d?.configured===true,
  active:d?.active===true,
  cadence:typeof d?.cadence==='string'?d.cadence:'Not configured',
  lastStatus:typeof d?.last_status==='string'?d.last_status:null,
  lastFinishedAt:typeof d?.last_finished_at==='string'?d.last_finished_at:null,
  agentPaused:d?.agent_execution_paused!==false,
 };
}
